import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2, CalendarPlus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  lprReportBlockers,
  sksLabel,
  useAddLprDiagnose,
  useAddLprProcedure,
  useCreateLprKontakt,
  useDeleteLprDiagnose,
  useDeleteLprKontakt,
  useDeleteLprProcedure,
  useImportLprAppointments,
  useLprKontakter,
  useLprSksCodes,
  useLprSubmissions,
  useReportLprForloeb,
  useUpdateLprForloeb,
  useUpdateLprKontakt,
} from '@/hooks/useLpr';
import type { LprDiagnoseArt, LprForloeb, LprSksCode } from '@/types/lpr';

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function SksSelect({
  list,
  codes,
  value,
  onChange,
  locale,
  allowEmpty,
  id,
}: {
  list: string;
  codes: LprSksCode[];
  value: string;
  onChange: (code: string) => void;
  locale: string;
  allowEmpty?: boolean;
  id?: string;
}) {
  const options = codes.filter((c) => c.list === list);
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm focus:border-ds-teal focus:outline-none focus:ring-ds-teal text-ds-text"
    >
      {allowEmpty && <option value="">—</option>}
      {options.map((c) => (
        <option key={c.code} value={c.code}>
          {c.code} — {locale.startsWith('en') ? c.label_en : c.label_da}
        </option>
      ))}
    </select>
  );
}

export function LprForloebEditor({ forloeb }: { forloeb: LprForloeb }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { data: codes = [] } = useLprSksCodes();
  const { data: packed, isLoading: loadingKontakter } = useLprKontakter(forloeb.id);
  const { data: submissions = [] } = useLprSubmissions(forloeb.id);
  const updateForloeb = useUpdateLprForloeb(forloeb.id);
  const createKontakt = useCreateLprKontakt(forloeb);
  const updateKontakt = useUpdateLprKontakt(forloeb.id);
  const deleteKontakt = useDeleteLprKontakt(forloeb.id);
  const addDiagnose = useAddLprDiagnose(forloeb.id);
  const deleteDiagnose = useDeleteLprDiagnose(forloeb.id);
  const addProcedure = useAddLprProcedure(forloeb.id);
  const deleteProcedure = useDeleteLprProcedure(forloeb.id);
  const importAppts = useImportLprAppointments(forloeb.id);
  const report = useReportLprForloeb(forloeb.id);

  const kontakter = packed?.kontakter ?? [];
  const diagnoser = packed?.diagnoser ?? [];
  const procedurer = packed?.procedurer ?? [];

  const blockers = useMemo(
    () => lprReportBlockers(forloeb, kontakter, diagnoser),
    [forloeb, kontakter, diagnoser],
  );

  const [expanded, setExpanded] = useState<string | null>(null);
  const [payloadOpen, setPayloadOpen] = useState(false);

  async function saveForloeb(patch: Partial<LprForloeb>) {
    try {
      await updateForloeb.mutateAsync(patch);
      toast.success(t('lpr.saved'));
    } catch (err) {
      console.error('update lpr forløb failed', err);
      toast.error(t('lpr.save_error'));
    }
  }

  async function handleReport() {
    if (!confirm(t('lpr.confirm_report'))) return;
    try {
      await report.mutateAsync();
      toast.success(t('lpr.report_ok'));
      setPayloadOpen(true);
    } catch (err) {
      console.error('lpr report failed', err);
      toast.error(t('lpr.report_error'));
    }
  }

  async function handleImport() {
    try {
      const n = await importAppts.mutateAsync();
      toast.success(t('lpr.imported_appointments', { count: n }));
    } catch (err) {
      console.error('import appointments failed', err);
      toast.error(t('lpr.import_error'));
    }
  }

  async function handleAddKontakt() {
    const start = new Date();
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    try {
      const row = await createKontakt.mutateAsync({
        start_at: start.toISOString(),
        end_at: end.toISOString(),
      });
      setExpanded(row.id);
      toast.success(t('lpr.kontakt_added'));
    } catch (err) {
      console.error('add kontakt failed', err);
      toast.error(t('lpr.save_error'));
    }
  }

  const latest = submissions[0] ?? null;

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-ds-text">{t('lpr.forloeb_title')}</h2>
            <p className="mt-1 text-sm text-ds-muted">{t('lpr.poc_banner')}</p>
          </div>
          <LprStatusBadge status={forloeb.status} />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('lpr.field_sor')}
            </label>
            <Input
              className="mt-1"
              defaultValue={forloeb.sor_id ?? ''}
              onBlur={(e) => {
                const next = e.target.value.trim() || null;
                if (next !== (forloeb.sor_id ?? null)) void saveForloeb({ sor_id: next });
              }}
              placeholder="SOR"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('lpr.field_forloebslabel')}
            </label>
            <SksSelect
              list="forloeb.label"
              codes={codes}
              value={forloeb.forloebslabel}
              locale={locale}
              onChange={(code) => void saveForloeb({ forloebslabel: code })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('lpr.field_henvisningstidspunkt')}
            </label>
            <input
              type="datetime-local"
              defaultValue={toLocalInput(forloeb.henvisningstidspunkt)}
              onBlur={(e) => {
                const iso = fromLocalInput(e.target.value);
                if (iso) void saveForloeb({ henvisningstidspunkt: iso, start_at: iso });
              }}
              className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm focus:border-ds-teal focus:outline-none focus:ring-ds-teal text-ds-text"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('lpr.field_henvisningsmaade')}
            </label>
            <SksSelect
              list="admin.henvmaade"
              codes={codes}
              value={forloeb.henvisningsmaade}
              locale={locale}
              onChange={(code) => void saveForloeb({ henvisningsmaade: code })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('lpr.field_frit_valg')}
            </label>
            <SksSelect
              list="admin.fritvalg"
              codes={codes}
              value={forloeb.frit_valg}
              locale={locale}
              onChange={(code) => void saveForloeb({ frit_valg: code })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('lpr.field_afslutningsmaade')}
            </label>
            <SksSelect
              list="admin.afslutning"
              codes={codes}
              value={forloeb.afslutningsmaade ?? ''}
              locale={locale}
              allowEmpty
              onChange={(code) =>
                void saveForloeb({
                  afslutningsmaade: code || null,
                  end_at: code ? new Date().toISOString() : null,
                })
              }
            />
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-medium text-ds-text">{t('lpr.kontakter_title')}</h2>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => void handleImport()} disabled={importAppts.isPending}>
              <CalendarPlus className="mr-1 h-4 w-4" />
              {t('lpr.import_appointments')}
            </Button>
            <Button size="sm" onClick={() => void handleAddKontakt()} disabled={createKontakt.isPending}>
              <Plus className="mr-1 h-4 w-4" />
              {t('lpr.add_kontakt')}
            </Button>
          </div>
        </div>

        {loadingKontakter ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-ds-muted" />
          </div>
        ) : kontakter.length === 0 ? (
          <p className="mt-4 text-sm text-ds-muted">{t('lpr.kontakter_empty')}</p>
        ) : (
          <ul className="mt-4 divide-y divide-ds-line">
            {kontakter.map((k) => {
              const kDiag = diagnoser.filter((d) => d.kontakt_id === k.id);
              const kProc = procedurer.filter((p) => p.kontakt_id === k.id);
              const open = expanded === k.id;
              return (
                <li key={k.id} className="py-3">
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : k.id)}
                    className="flex w-full items-center justify-between text-left"
                  >
                    <span className="text-sm font-medium text-ds-text">
                      {new Date(k.start_at).toLocaleString(locale)} ·{' '}
                      {sksLabel(codes, k.kontakt_type, locale)}
                    </span>
                    <span className="text-xs text-ds-muted">
                      {kDiag.some((d) => d.art === 'aktion')
                        ? t('lpr.has_aktion')
                        : t('lpr.missing_aktion')}
                    </span>
                  </button>
                  {open && (
                    <div className="mt-3 space-y-3 rounded-md bg-ds-sunk p-3 bg-ds-card/60">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="block text-xs font-medium text-ds-muted">
                            {t('lpr.field_kontakt_type')}
                          </label>
                          <SksSelect
                            list="kontakt.type"
                            codes={codes}
                            value={k.kontakt_type}
                            locale={locale}
                            onChange={(code) => void updateKontakt.mutateAsync({ id: k.id, kontakt_type: code })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-ds-muted">
                            {t('lpr.field_betaling')}
                          </label>
                          <SksSelect
                            list="admin.betaling"
                            codes={codes}
                            value={k.betaling}
                            locale={locale}
                            onChange={(code) => void updateKontakt.mutateAsync({ id: k.id, betaling: code })}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-ds-muted">
                            {t('lpr.field_start')}
                          </label>
                          <input
                            type="datetime-local"
                            defaultValue={toLocalInput(k.start_at)}
                            onBlur={(e) => {
                              const iso = fromLocalInput(e.target.value);
                              if (iso) void updateKontakt.mutateAsync({ id: k.id, start_at: iso });
                            }}
                            className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-ds-muted">
                            {t('lpr.field_end')}
                          </label>
                          <input
                            type="datetime-local"
                            defaultValue={toLocalInput(k.end_at)}
                            onBlur={(e) => {
                              const iso = fromLocalInput(e.target.value);
                              if (iso) void updateKontakt.mutateAsync({ id: k.id, end_at: iso });
                            }}
                            className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm text-ds-text"
                          />
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-medium text-ds-muted">
                          {t('lpr.diagnoses')}
                        </p>
                        <ul className="mt-1 space-y-1">
                          {kDiag.map((d) => (
                            <li key={d.id} className="flex items-center justify-between text-sm">
                              <span className="text-ds-text-2">
                                {d.art === 'aktion' ? t('lpr.art_aktion') : t('lpr.art_bi')}:{' '}
                                {sksLabel(codes, d.sks_code, locale)}
                              </span>
                              <button
                                type="button"
                                className="text-ds-muted hover:text-ds-red-ink"
                                onClick={() => void deleteDiagnose.mutateAsync(d.id)}
                                aria-label={t('lpr.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                        <AddCodedRow
                          codes={codes.filter((c) => c.list === 'diag')}
                          locale={locale}
                          onAdd={(code, label) => {
                            const art: LprDiagnoseArt = kDiag.some((d) => d.art === 'aktion')
                              ? 'bi'
                              : 'aktion';
                            void addDiagnose.mutateAsync({
                              clinic_id: forloeb.clinic_id,
                              kontakt_id: k.id,
                              art,
                              sks_code: code,
                              sks_text: label,
                            });
                          }}
                          label={t('lpr.add_diagnose')}
                        />
                      </div>

                      <div>
                        <p className="text-xs font-medium text-ds-muted">
                          {t('lpr.procedures')}
                        </p>
                        <ul className="mt-1 space-y-1">
                          {kProc.map((p) => (
                            <li key={p.id} className="flex items-center justify-between text-sm">
                              <span className="text-ds-text-2">
                                {sksLabel(codes, p.sks_code, locale)}
                              </span>
                              <button
                                type="button"
                                className="text-ds-muted hover:text-ds-red-ink"
                                onClick={() => void deleteProcedure.mutateAsync(p.id)}
                                aria-label={t('lpr.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                        <AddCodedRow
                          codes={codes.filter((c) => c.list === 'proc')}
                          locale={locale}
                          onAdd={(code, label) =>
                            void addProcedure.mutateAsync({
                              clinic_id: forloeb.clinic_id,
                              kontakt_id: k.id,
                              sks_code: code,
                              sks_text: label,
                              performed_at: k.start_at,
                            })
                          }
                          label={t('lpr.add_procedure')}
                        />
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (!confirm(t('lpr.confirm_delete_kontakt'))) return;
                          void deleteKontakt.mutateAsync(k.id);
                        }}
                      >
                        {t('lpr.delete_kontakt')}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
        <h2 className="text-lg font-medium text-ds-text">{t('lpr.report_title')}</h2>
        {blockers.length > 0 ? (
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-ds-amber-ink">
            {blockers.map((b) => (
              <li key={b.key}>{t(`lpr.blocker_${b.key}`)}</li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-ds-green-ink">{t('lpr.ready_to_report')}</p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={() => void handleReport()}
            disabled={blockers.length > 0 || report.isPending}
          >
            {report.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('lpr.report')}
          </Button>
        </div>
        {latest && (
          <div className="mt-4">
            <p className="text-sm text-ds-muted">
              {t('lpr.last_report', {
                version: latest.version_number,
                at: new Date(latest.created_at).toLocaleString(locale),
              })}
            </p>
            <button
              type="button"
              className="mt-2 text-sm text-ds-teal hover:underline"
              onClick={() => setPayloadOpen((v) => !v)}
            >
              {payloadOpen ? t('lpr.hide_payload') : t('lpr.show_payload')}
            </button>
            {payloadOpen && (
              <pre className="mt-2 max-h-80 overflow-auto rounded-md bg-ds-sunk p-3 text-xs text-ds-text">
                {JSON.stringify(latest.payload, null, 2)}
              </pre>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function AddCodedRow({
  codes,
  locale,
  onAdd,
  label,
}: {
  codes: LprSksCode[];
  locale: string;
  onAdd: (code: string, label: string) => void;
  label: string;
}) {
  const [code, setCode] = useState(codes[0]?.code ?? '');
  return (
    <div className="mt-2 flex gap-2">
      <select
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className="block w-full rounded-md border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text"
      >
        {codes.map((c) => (
          <option key={c.code} value={c.code}>
            {c.code} — {locale.startsWith('en') ? c.label_en : c.label_da}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        variant="secondary"
        disabled={!code}
        onClick={() => {
          const row = codes.find((c) => c.code === code);
          if (!row) return;
          onAdd(row.code, locale.startsWith('en') ? row.label_en : row.label_da);
        }}
      >
        {label}
      </Button>
    </div>
  );
}

export function LprStatusBadge({ status }: { status: LprForloeb['status'] }) {
  const { t } = useTranslation();
  const cls =
    status === 'reported'
      ? 'bg-ds-green-tint text-ds-green-ink'
      : status === 'ready'
        ? 'bg-ds-teal-tint text-ds-teal'
        : 'bg-ds-neutral-tint text-ds-text bg-ds-card';
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {t(`lpr.status_${status}`)}
    </span>
  );
}
